// src/main.js
import "@/assets/main.css";
import { heritageTheme } from './heritageTheme';

import { userManager } from "@/auth/oidc";
import { createPinia } from "pinia";
import { createApp, h } from "vue"; // <-- import h
import App from "./App.vue";
import router from "./router";
import { useAuthStore } from "@/stores/auth";

import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "@mdi/font/css/materialdesignicons.css"; // keep if you use MDI elsewhere
import * as labsComponents from "vuetify/labs/components";
import "leaflet/dist/leaflet.css";

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

const myCustomLightTheme = {
  dark: false,
  colors: {
    background: "#FFFFFF",
    surface: "#FFFFFF",
    "surface-bright": "#FFFFFF",
    "surface-light": "#EEEEEE",
    "surface-variant": "#424242",
    "on-surface-variant": "#EEEEEE",
    primary: "#1867C0",
    "primary-darken-1": "#1F5592",
    secondary: "#48A9A6",
    "secondary-darken-1": "#018786",
    error: "#B00020",
    info: "#2196F3",
    success: "#4CAF50",
    warning: "#FB8C00",
  },
  variables: {
    "border-color": "#000000",
    "border-opacity": 0.12,
    "border-radius": 15,
    "high-emphasis-opacity": 0.87,
    "medium-emphasis-opacity": 0.6,
    "disabled-opacity": 0.38,
    "idle-opacity": 0.04,
    "hover-opacity": 0.04,
    "focus-opacity": 0.12,
    "selected-opacity": 0.08,
    "activated-opacity": 0.12,
    "pressed-opacity": 0.12,
    "dragged-opacity": 0.08,
    "theme-kbd": "#212529",
    "theme-on-kbd": "#FFFFFF",
    "theme-code": "#F5F5F5",
    "theme-on-code": "#000000",
  },
};

const vuetify = createVuetify({
  theme: {
    defaultTheme: "myCustomLightTheme",
    themes: { myCustomLightTheme: { ...myCustomLightTheme, ...heritageTheme } },
  },
  // --- pick ONE icons block. This one uses Font Awesome classes:
  icons: {
    defaultSet: "fa",
    sets: {
      fa: {
        component: (props) => h("i", { class: props.icon }),
      },
    },
  },
  // If you prefer MDI instead, replace the above with:
  icons: { defaultSet: "mdi" },
  defaults: {
    VTextField: {
      variant: "outlined",
      density: "comfortable",
      class: "mb-4",
      color: "primary",
      hideDetails: "auto",
    },
    VTextarea: {
      variant: "outlined",
      density: "comfortable",
      class: "mb-4",
      color: "primary",
      hideDetails: "auto",
    },
    VSelect: {
      variant: "outlined",
      density: "comfortable",
      class: "mb-4",
      color: "primary",
      hideDetails: "auto",
    },
  },
  components: {
    ...components,
    ...labsComponents,
  },
  directives,
});

const app = createApp(App);
const pinia = createPinia();

app.use(Toast, {
  position: "top-right",
  timeout: 2500,
  closeOnClick: true,
  pauseOnHover: true,
  hideProgressBar: true,
  maxToasts: 5,
  newestOnTop: true,
  // ensure it's above dialogs/overlays
  toastDefaults: { containerClassName: "ww-toasts" },
});

app.use(pinia);
app.use(router);
app.use(vuetify);

// Bootstrap without top-level await
(async () => {
  try {
    //await useSettingsStore().init();
  } catch (e) {
    console.error("Settings init failed", e);
  } finally {
    app.mount("#app");
  }

  const auth = useAuthStore();

  userManager.events.addAccessTokenExpired(() => {
    userManager.signinSilent().catch((err) => console.error("Silent renew failed", err));
  });

  userManager.events.addSilentRenewError((err) => {
    console.error("Silent renew error", err);
    auth.user = null;
    auth.login();
  });

  userManager.events.addUserSignedOut(() => {
    console.log("Remote session ended, clearing local user");
    auth.user = null;
    auth.login();
  });

  window.addEventListener("online", async () => {
    try {
      const user = await userManager.signinSilent();
      console.log("Silent renew (online) successful", user);
    } catch (err) {
      console.error("Silent renew (online) failed", err);
    }
  });
})();
